import { useAuth } from '../stores/auth';

const BASE_URL =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  (location.hostname === 'localhost' ? 'http://localhost/ciudadapp/api' : '/api');

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

// Serializa refreshes simultáneos: si hay uno en vuelo, todos esperan el mismo resultado
let _refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (_refreshInFlight) return _refreshInFlight;

  const { refreshToken, ciudadano } = useAuth.getState();
  if (!refreshToken || !ciudadano) return false;

  _refreshInFlight = (async () => {
    try {
      const res = await fetch(BASE_URL + '/auth?action=refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      });
      if (!res.ok) { useAuth.getState().logout(); return false; }
      const json = await res.json();
      if (!json.data?.access) { useAuth.getState().logout(); return false; }
      useAuth.getState().setSession(json.data.access, json.data.refresh, ciudadano);
      return true;
    } catch {
      useAuth.getState().logout();
      return false;
    } finally {
      _refreshInFlight = null;
    }
  })();

  return _refreshInFlight;
}

export async function api<T = unknown>(
  path: string,
  options: RequestInit & { auth?: boolean; _retry?: boolean } = {}
): Promise<T> {
  const { auth = true, headers, _retry = false, ...rest } = options;
  const token = useAuth.getState().accessToken;

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(headers as Record<string, string> | undefined),
  };
  if (!(rest.body instanceof FormData) && rest.body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (auth && token) {
    finalHeaders['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(BASE_URL + path, { ...rest, headers: finalHeaders });

  let json: any;
  try {
    json = await res.json();
  } catch {
    throw new ApiError('Respuesta inválida del servidor', res.status, null);
  }

  if (!res.ok || json.ok === false) {
    if (res.status === 401 && auth && !_retry) {
      const ok = await tryRefresh();
      if (ok) return api<T>(path, { ...options, _retry: true });
    }
    if (res.status === 401) useAuth.getState().logout();
    throw new ApiError(json.error || json.message || `HTTP ${res.status}`, res.status, json);
  }

  return json.data as T;
}

export function apiUrl(path: string): string {
  return BASE_URL + path;
}
